import { FormEvent, useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@itaas/core/auth/useAuth";
import { saveReturnToModuleId, saveReturnToPath } from "@itaas/core/modules/suite-navigation";
import { getPort } from "@itaas/ports";
import {
  DEMO_TENANT_PASSWORD,
  SPECIALTY_DEMO_TENANTS,
} from "@itaas/core/demo/demo-tenants.catalog";
import { getPlatformBaseDomain } from "@itaas/core/tenant";
import { IAM_API_BASE_URL } from "@/config";
import { SuiteAuthShell } from "@/components/SuiteAuthShell";
import { isProductionBuild, showSuiteDevHints } from "@/config/env";
import {
  getPostLoginAction,
  type PostLoginAction,
} from "@/navigation/post-login";
import { routes } from "@/router/all_routes";
import { suiteAuthForceRefreshSession } from "@/auth/suite-auth.service";

const GENERIC_DEMO = { email: "admin@itaas.demo", password: DEMO_TENANT_PASSWORD };

function applyPostLoginAction(action: PostLoginAction): string | null {
  if (action.kind === "handoff") {
    window.location.replace(action.url);
    return null;
  }
  return action.path;
}

export function LoginPage() {
  const { login, isAuthenticated, isLoading, verifySession } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(showSuiteDevHints ? GENERIC_DEMO.email : "");
  const [password, setPassword] = useState(showSuiteDevHints ? GENERIC_DEMO.password : "");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [iamOffline, setIamOffline] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);
  const [awaitingHandoff, setAwaitingHandoff] = useState(false);

  const returnModule = searchParams.get("module");
  const returnTo = searchParams.get("returnTo");
  const stayOnLogin = searchParams.get("stay") === "1";
  const refreshSession = searchParams.get("refresh") === "1";
  const [sessionSynced, setSessionSynced] = useState(!refreshSession);

  useEffect(() => {
    const emailParam = searchParams.get("email")?.trim().toLowerCase();
    const tenantParam = searchParams.get("tenant")?.trim().toLowerCase();
    if (emailParam) {
      setEmail(emailParam);
    }
    if (tenantParam) {
      try {
        sessionStorage.setItem("itaas.login.tenantSlug", tenantParam);
      } catch {
        /* ignore */
      }
    }
  }, [searchParams]);

  useEffect(() => {
    fetch(`${IAM_API_BASE_URL}/health`)
      .then((r) => {
        if (!r.ok) setIamOffline(true);
      })
      .catch(() => setIamOffline(true));
  }, []);

  useEffect(() => {
    if (returnModule) {
      saveReturnToModuleId(returnModule);
    }
    if (returnTo?.startsWith("/")) {
      saveReturnToPath(returnTo);
    }
  }, [returnModule, returnTo]);

  useEffect(() => {
    if (!refreshSession) return;
    void suiteAuthForceRefreshSession()
      .catch(() => undefined)
      .finally(() => setSessionSynced(true));
  }, [refreshSession]);

  useEffect(() => {
    if (returnModule || returnTo) {
      void verifySession();
    }
  }, [returnModule, returnTo, verifySession]);

  useEffect(() => {
    if (stayOnLogin || !sessionSynced) return;
    if (!isLoading && isAuthenticated && !redirectPath && !awaitingHandoff) {
      try {
        const action = getPostLoginAction();
        if (action.kind === "handoff") {
          setAwaitingHandoff(true);
          window.location.replace(action.url);
        } else {
          setRedirectPath(action.path);
        }
      } catch {
        setRedirectPath(routes.modules);
      }
    }
  }, [isAuthenticated, isLoading, redirectPath, awaitingHandoff, stayOnLogin, sessionSynced]);

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  if (!isLoading && isAuthenticated) {
    return (
      <div className="suite-center-message">
        <p className="text-muted mb-3">
          {awaitingHandoff
            ? "Ouverture du module…"
            : "Redirection vers votre module…"}
        </p>
        {awaitingHandoff && !isProductionBuild && (
          <p className="text-muted small">
            Si rien ne se passe, démarrez l&apos;app produit (ex. Inventory sur le
            port {getPort("inventory", "web")}) ou{" "}
            <a href={routes.modules}>choisissez un module</a>.
          </p>
        )}
      </div>
    );
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const tenantFromUrl = searchParams.get("tenant")?.trim().toLowerCase();
      let tenantSlug = tenantFromUrl;
      if (!tenantSlug) {
        try {
          tenantSlug =
            sessionStorage.getItem("itaas.login.tenantSlug")?.trim().toLowerCase() ||
            undefined;
        } catch {
          tenantSlug = undefined;
        }
      }
      await login({
        email,
        password,
        ...(tenantSlug ? { tenantSlug } : {}),
      });
      const path = applyPostLoginAction(getPostLoginAction());
      if (path) {
        setRedirectPath(path);
      } else {
        setAwaitingHandoff(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Connexion impossible");
      setSubmitting(false);
    }
  };

  return (
    <SuiteAuthShell
      heroTitle={
        <>
          Une plateforme,
          <br />
          tous vos métiers.
        </>
      }
      heroSubtitle="Connectez-vous une fois, accédez à tous vos modules licenciés — RH, stock, écoles, mines et plus encore."
      heroExtra={
        <ul className="suite-auth-hero__highlights">
          <li>Compte unique & sécurisé (IAM)</li>
          <li>Accès immédiat à vos applications</li>
          <li>Multi-tenant & conforme</li>
        </ul>
      }
    >
      <div className="suite-auth-form">
        <header className="suite-auth-form-intro text-center">
          <p className="suite-auth-form__eyebrow mb-2">Espace sécurisé</p>
          <h2 className="suite-auth-form__title mb-2">Connexion</h2>
          <p className="suite-auth-form__lead mb-0">
            Saisissez vos identifiants pour accéder à votre espace
          </p>
        </header>

        {iamOffline && (
          <div className="alert alert-warning py-2 small mb-3">
            {isProductionBuild ? (
              <>Le service d&apos;authentification est temporairement indisponible. Réessayez dans quelques instants.</>
            ) : (
              <>
                L&apos;API IAM ne répond pas (port <strong>{getPort("iam", "api")}</strong>).
                <br />
                <code>cd products/itaas-iam; pnpm dev:api</code>
              </>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label" htmlFor="email">
              Email
            </label>
            <div className="input-group">
              <input
                id="email"
                type="email"
                className="form-control border-end-0"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
              <span className="input-group-text border-start-0">
                <i className="ti ti-mail" aria-hidden />
              </span>
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label" htmlFor="password">
              Mot de passe
            </label>
            <div className="suite-pass-group">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                className="form-control suite-pass-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className="suite-pass-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              >
                <i className={`ti ${showPassword ? "ti-eye" : "ti-eye-off"}`} aria-hidden />
              </button>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger py-2" role="alert">
              {error}
            </div>
          )}

          <div className="mb-3">
            <button
              type="submit"
              className="btn btn-primary w-100 suite-auth-submit"
              disabled={submitting}
            >
              {submitting ? "Connexion…" : "Se connecter"}
            </button>
          </div>
        </form>

        {showSuiteDevHints && (
          <div className="suite-dev-hints">
            <p className="mb-2 fw-semibold small">Démo générique (tous modules)</p>
            <p className="mb-2">
              <code>{GENERIC_DEMO.email}</code> / <code>{GENERIC_DEMO.password}</code>
            </p>
            <p className="mb-2 fw-semibold small">Démos métier — admin (accès complet)</p>
            <ul className="list-unstyled mb-3 small">
              {SPECIALTY_DEMO_TENANTS.map((tenant) => (
                <li key={tenant.slug} className="mb-1">
                  <code>{tenant.adminEmail}</code>
                  <span className="text-muted"> — {tenant.label}</span>
                </li>
              ))}
            </ul>
            <p className="mb-2 fw-semibold small">Profils restreints (présentation publique)</p>
            {SPECIALTY_DEMO_TENANTS.map((tenant) => (
              <details key={tenant.slug} className="suite-dev-hints__tenant mb-2 small">
                <summary className="text-muted">{tenant.name}</summary>
                <ul className="list-unstyled mb-0 mt-1 ps-2">
                  {tenant.profileAccounts.map((profile) => (
                    <li key={profile.email} className="mb-1">
                      <code>{profile.email}</code>
                      <span className="text-muted"> — {profile.label}</span>
                    </li>
                  ))}
                </ul>
              </details>
            ))}
            <p className="mb-0 mt-2">
              Mot de passe démo : <code>{GENERIC_DEMO.password}</code>
              {" · "}
              Trial : <code>{`admin@{slug}.${getPlatformBaseDomain()}`}</code> /{" "}
              <code>Welcome123!</code>
            </p>
          </div>
        )}
      </div>
    </SuiteAuthShell>
  );
}
